import { Grid, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo } from 'react';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import {
	type ResetPasswordDto,
	useResetPasswordForm,
} from '../hooks/useResetPasswordForm';
import { useServerAccountValidation } from '../hooks/useServerAccountValidation';
import { PasswordInputs } from './PasswordInputs';

interface ResetPasswordModalProps {
	close: () => void;
	opened: boolean;
	organisation?: string;
	accountId?: string; // Empty account ID will use of logged in user
}

export function ResetPasswordModal({
	opened,
	close,
	accountId,
	organisation,
}: ResetPasswordModalProps) {
	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useResetPasswordForm(accountId);

	const resetPasswordUrl = useMemo(() => {
		if (accountId) return `${organisation}/user/reset-password`;

		return `${organisation}/user/reset-password/self`;
	}, [accountId]);

	const editAccountMutation = mutate<ResetPasswordDto>(
		resetPasswordUrl,
		inputs,
	);

	useServerAccountValidation(editAccountMutation, form);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={resetAndClose}
				title={<Title label={'Reset Password'} />}
				w={100}
				withCloseButton={true}
				size={'sm'}
				centered
			>
				<LoadingOverlay visible={editAccountMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={resetAndClose}
					title={'Password Reset!'}
					body={'Password has been successfully reset. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editAccountMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<Grid>
						<PasswordInputs form={form} />
					</Grid>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
